import { Box } from '@mui/material';
import { Children, Fragment, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { tokens } from '@synergy/theme-palo';

const c = tokens.color;

/**
 * How much a notice weighs. The three values match what is maintained on the
 * master record, where a notice is filed as blue, orange or red (Roland
 * Niedermayr, 2026-09-02).
 */
export type PaloNoteTone = 'info' | 'warn' | 'danger';

/**
 * The mark of a notice: a circle with an "i", a triangle with an "!", a circle
 * with a cross (GF 12.10.2026).
 *
 * It replaces the words "STAMMDATEN" and "BELEG" that stood in front of every
 * notice. Those said where a notice came from, which the two groups now say;
 * the mark says how much it weighs, which is what is looked for first.
 *
 * Every notice is amber now, the one colour for "please mind" (colour concept,
 * GF 15.10.2026, #2290). The blue and red ones are gone, so the weight is in
 * the shape alone.
 */
const PaloNoteMark = ({ tone }: { tone: PaloNoteTone }): ReactElement => {
    const ink = c.warn;

    if ('warn' === tone) {
        return (
            <Box component="svg" viewBox="0 0 16 16" sx={{ width: 15, height: 15, flexShrink: 0 }} aria-hidden>
                <path d="M8 1.4 15.1 13.9a.9.9 0 0 1-.8 1.3H1.7a.9.9 0 0 1-.8-1.3Z" fill={ink} />
                <rect x="7.15" y="5.3" width="1.7" height="4.6" rx=".85" fill="#FFFFFF" />
                <circle cx="8" cy="11.9" r="1" fill="#FFFFFF" />
            </Box>
        );
    }

    return (
        <Box component="svg" viewBox="0 0 16 16" sx={{ width: 15, height: 15, flexShrink: 0 }} aria-hidden>
            <circle cx="8" cy="8" r="7" fill={ink} />
            {'danger' === tone ? (
                <path
                    d="M5.5 5.5 10.5 10.5M10.5 5.5 5.5 10.5"
                    stroke="#FFFFFF"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                />
            ) : (
                <>
                    <circle cx="8" cy="4.6" r="1" fill="#FFFFFF" />
                    <rect x="7.15" y="6.5" width="1.7" height="5" rx=".85" fill="#FFFFFF" />
                </>
            )}
        </Box>
    );
};

export interface PaloNoticeProps {
    tone?: PaloNoteTone;
    /**
     * The opening line of the notice, set in bold before the text. Use
     * `paloNoticeText` to take it off the written notice.
     */
    title?: ReactNode;
    /** Edit affordance beside the source. */
    onEdit?: () => void;
    editLabel?: ReactNode;
}

/**
 * A notice in the head of the Belegmaske, written out in full, in one line of
 * text: its mark, what it is called, and the whole of it.
 *
 * It was a pill that showed its first line and opened the rest on a click. The
 * key users asked for the opposite — "dominant und komplett ausgeben" (meeting
 * 2026-09-22): a notice is there to be read before anything is done on the
 * document, and a text that has to be opened first is a text that is skipped.
 * Written over several lines it then took the height of a letter in a bar that
 * never scrolls, so it is set as flowing text instead (FRM-03, GF 12.10.2026).
 */
export const PaloNotice = ({
    tone = 'info',
    title,
    onEdit,
    editLabel,
    children,
}: PropsWithChildren<PaloNoticeProps>): ReactElement => {
    return (
        <Box role="note" sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
            {/* Centred on the first line of the text, not on the notice: a
                notice that runs over three lines would otherwise carry its
                mark in the middle of the paragraph. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: `${12 * 1.35}px`,
                    flexShrink: 0,
                }}
            >
                <PaloNoteMark tone={tone} />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    paddingLeft: '8px',
                    borderLeft: `4px solid ${c.warn}`,
                    color: c.warnText,
                    fontSize: '12px',
                    lineHeight: 1.35,
                    wordBreak: 'break-word',
                    // Markdown arrives wrapped in paragraphs, inside a box of
                    // its own. Both run on in one line here, parted by the mark
                    // that stands for the break that was written.
                    '& .markdown-converter': { display: 'inline' },
                    '& p': { display: 'inline', margin: 0 },
                    '& p + p::before': { content: '" · "', color: c.warn },
                }}
            >
                {title && (
                    <Box component="span" sx={{ fontWeight: 700, marginRight: '4px', color: c.warnInk }}>
                        {title}
                    </Box>
                )}
                {children}
                {onEdit && (
                    <Box
                        component="span"
                        role="button"
                        onClick={onEdit}
                        sx={{
                            fontSize: '11px',
                            color: c.warnInk,
                            cursor: 'pointer',
                            marginLeft: '6px',
                            whiteSpace: 'nowrap',
                            '&:hover': { color: c.warnText, textDecoration: 'underline' },
                        }}
                    >
                        {editLabel}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export interface PaloNoticesProps {
    /**
     * The notices, in groups — on a document the master record's first, the
     * document's own below. An empty group is dropped, and what is left is
     * parted by a line, so a single group looks as it did (GF 12.10.2026).
     */
    groups: ReactNode[];
    /** Offer to write a new notice, under the ones there are. */
    action?: ReactNode;
}

/**
 * The notices of a document, two to a row.
 *
 * They stand in the part of the mask that never scrolls, so their height is
 * the form's height. One under the other, three of them took 228 pixels of a
 * 13-inch screen and the third was folded away behind a counter — which is
 * what a notice may never be: "Hinweise dürfen nie eingeklappt werden" (GF
 * 12.10.2026). Two columns and one line of text each hold four notices in
 * about a hundred pixels, so all of them can stand open (FRM-03, #2236).
 */
export const PaloNotices = ({ groups, action }: PaloNoticesProps): ReactElement => {
    const filled = groups.map(group => Children.toArray(group).filter(Boolean)).filter(notices => notices.length > 0);

    return (
        <Box
            sx={{
                backgroundColor: c.warnSoft,
                border: `1px solid ${c.warnLine}`,
                borderRadius: tokens.radius.small + 'px',
                padding: '7px 10px',
            }}
        >
            {filled.map((notices, index) => (
                <Fragment key={index}>
                    {index > 0 && <Box sx={{ height: '1px', backgroundColor: c.warnLine, margin: '7px 0' }} />}
                    <Box
                        sx={{
                            display: 'grid',
                            // A single notice has the width of the bar; from
                            // the second on they stand side by side, and every
                            // further pair adds a row.
                            gridTemplateColumns: 1 === notices.length ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                            columnGap: '16px',
                            rowGap: '6px',
                        }}
                    >
                        {notices}
                    </Box>
                </Fragment>
            ))}
            {action && <Box sx={{ marginTop: filled.length > 0 ? '6px' : 0 }}>{action}</Box>}
        </Box>
    );
};
